import { useState } from "react";
import { ArrowRight, Download, MapPin, Phone, X, ChevronLeft, ChevronRight } from "lucide-react";
import { mediaUrl } from "@/lib/api";

const maps = (q) => `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}`;

const Text = ({ d }) => (
  <div className="max-w-2xl">
    {d.heading && <h3 className="font-serif-d mb-3 text-2xl sm:text-3xl">{d.heading}</h3>}
    {d.body && <p className="whitespace-pre-line text-[17px] leading-relaxed text-slate-600">{d.body}</p>}
  </div>
);

const Lightbox = ({ images, idx, setIdx }) => (
  <div className="fixed inset-0 z-[80] flex items-center justify-center bg-[rgba(15,23,42,.92)] p-4" onClick={() => setIdx(null)} data-testid="gallery-lightbox">
    <button className="absolute right-4 top-4 rounded-full bg-white/10 p-2 text-white" aria-label="Close" data-testid="gallery-lightbox-close"><X /></button>
    {images.length > 1 && <button className="absolute left-3 rounded-full bg-white/10 p-2 text-white" onClick={(e) => { e.stopPropagation(); setIdx((idx - 1 + images.length) % images.length); }} data-testid="gallery-prev"><ChevronLeft /></button>}
    <img src={mediaUrl(images[idx])} alt="" className="max-h-[85vh] max-w-full rounded-xl object-contain" onClick={(e) => e.stopPropagation()} />
    {images.length > 1 && <button className="absolute right-3 rounded-full bg-white/10 p-2 text-white" onClick={(e) => { e.stopPropagation(); setIdx((idx + 1) % images.length); }} data-testid="gallery-next"><ChevronRight /></button>}
  </div>
);

const Gallery = ({ d }) => {
  const [idx, setIdx] = useState(null);
  const images = d.images || [];
  return (
    <>
      <div className="flex snap-x snap-mandatory gap-3 overflow-x-auto pb-2">
        {images.map((u, i) => (
          <button key={`${u}-${i}`} onClick={() => setIdx(i)} className="group relative h-72 w-56 shrink-0 snap-start overflow-hidden rounded-2xl sm:h-80 sm:w-64" data-testid={`gallery-image-${i}`}>
            <img src={mediaUrl(u)} alt="" loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
          </button>
        ))}
      </div>
      {idx !== null && <Lightbox images={images} idx={idx} setIdx={setIdx} />}
    </>
  );
};

const Swatches = ({ d }) => (
  <div>
    {d.title && <div className="eyebrow mb-3">{d.title}</div>}
    <div className="flex flex-wrap gap-4">
      {(d.colors || []).map((c, i) => (
        <div key={`${c.hex}-${c.name}-${i}`} className="flex flex-col items-center gap-2" data-testid={`swatch-${i}`}>
          <div className="h-16 w-16 rounded-full border border-[var(--line)] shadow-inner" style={{ background: c.hex }} />
          <span className="text-xs text-slate-600">{c.name}</span>
        </div>
      ))}
    </div>
  </div>
);

const TONES = { wine: "bg-[var(--wine)] text-[var(--ivory)]", olive: "bg-[var(--olive)] text-[var(--ivory)]", ivory: "bg-[var(--ivory-2)] text-[var(--ink)]" };

const Card = ({ d }) => (
  <a href={d.link || undefined} className={`grain relative block overflow-hidden rounded-[22px] p-7 transition-transform duration-300 hover:-translate-y-0.5 sm:p-9 ${TONES[d.tone] || TONES.ivory}`} data-testid="content-card">
    <img src="/media/monogram.png" alt="" className="pointer-events-none absolute -bottom-6 -right-6 h-40 w-40 opacity-[.13]" />
    <div className="flex items-start justify-between">
      <div className={`text-[11px] uppercase tracking-[.28em] ${d.tone === "ivory" ? "text-[var(--gold-deep)]" : "text-[var(--gold)]"}`}>{d.eyebrow}</div>
      {d.link && <ArrowRight size={18} className="opacity-70" />}
    </div>
    <div className="font-serif-d mt-3 text-[28px] leading-tight">{d.title}</div>
    {d.subtitle && <div className="mt-1 text-sm opacity-75">{d.subtitle}</div>}
    {d.body && <p className="relative mt-5 max-w-xl leading-relaxed opacity-90">{d.body}</p>}
  </a>
);

const Day = ({ d }) => (
  <div className="grid grid-cols-[64px_1fr] gap-4 sm:grid-cols-[96px_1fr] sm:gap-8" data-testid="itinerary-day">
    <div className="pt-1 text-center">
      <div className="font-serif-d text-4xl leading-none sm:text-5xl">{(d.date_label || "").split(" ")[0]}</div>
      <div className="mt-1 text-xs uppercase tracking-[.25em] text-[var(--gold-deep)]">{(d.date_label || "").split(" ").slice(1).join(" ")}</div>
    </div>
    <div className="border-l border-[var(--line)] pl-5 sm:pl-8">
      <div className="font-serif-d text-2xl">{d.title}</div>
      {d.weekday && <div className="text-sm text-slate-500">{d.weekday}</div>}
      <div className="mt-5 space-y-4">
        {(d.events || []).map((e, i) => (
          <div key={`${e.time}-${e.title}-${i}`} className="ivory-card p-5">
            <div className="text-xs uppercase tracking-[.2em] text-[var(--gold-deep)]">{e.time}</div>
            {e.link ? (
              <a href={e.link} className="group mt-1 inline-flex items-center gap-2 text-lg underline-offset-4 hover:underline" data-testid="event-page-link">
                {e.title} <ArrowRight size={16} className="text-[var(--gold-deep)] transition-transform group-hover:translate-x-0.5" />
              </a>
            ) : <div className="mt-1 text-lg">{e.title}</div>}
            {e.note && <p className="mt-1 text-[15px] text-slate-600">{e.note}</p>}
            {e.link && <a href={e.link} className="mt-2 block text-xs uppercase tracking-[.2em] text-[var(--gold-deep)] hover:underline" data-testid="event-details-link">View event details</a>}
            {e.place && (
              <a href={maps(e.place)} target="_blank" rel="noreferrer" className="mt-3 inline-flex items-center gap-2 rounded-full border border-[var(--line)] px-3 py-1.5 text-sm transition-colors hover:border-[var(--gold)]" data-testid="place-map-link">
                <MapPin size={14} className="text-[var(--gold-deep)]" /> {e.place}
              </a>
            )}
          </div>
        ))}
      </div>
    </div>
  </div>
);

const Contact = ({ d }) => (
  <div className="ivory-card flex items-center justify-between gap-3 p-5" data-testid="contact-card">
    <div>
      <div className="text-lg">{d.name}</div>
      <div className="text-sm text-slate-500">{d.role}</div>
      <div className="mt-1 text-sm text-slate-700">{d.phone}</div>
    </div>
    {d.phone && (
      <a href={`tel:${d.phone.replace(/[^\d+]/g, "")}`} className="btn-ink !px-4 !py-2.5" data-testid="contact-call-btn"><Phone size={15} /> Call</a>
    )}
  </div>
);

const FileDl = ({ d }) => (
  <a href={mediaUrl(d.url)} target="_blank" rel="noreferrer" download={d.name} className="ivory-card flex items-center justify-between gap-3 p-5 transition-colors hover:border-[var(--gold)]" data-testid="download-file-link">
    <div>
      <div className="text-lg">{d.label || d.name}</div>
      {d.name && <div className="text-sm text-slate-500">{d.name}</div>}
    </div>
    <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[var(--ivory-2)] text-[var(--gold-deep)]"><Download size={18} /></span>
  </a>
);

const RENDER = {
  anchor: ({ d }) => <span id={(d.name || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")} className="block scroll-mt-24" aria-hidden="true" data-testid="section-anchor" />,
  text: Text, gallery: Gallery, swatches: Swatches, card: Card, day: Day, contact: Contact, file: FileDl,
  image: ({ d }) => (
    <figure>
      {d.url && <img src={mediaUrl(d.url)} alt={d.caption || ""} className="w-full rounded-[22px] object-cover" />}
      {d.caption && <figcaption className="mt-2 text-sm text-slate-500">{d.caption}</figcaption>}
    </figure>
  ),
  button: ({ d }) => <a href={d.url} target={d.url?.startsWith("#") ? undefined : "_blank"} rel="noreferrer" className="btn-ink" data-testid="content-button">{d.label} <ArrowRight size={15} /></a>,
};

export const SectionView = ({ section }) => {
  const C = RENDER[section.type];
  return C ? <C d={section.data || {}} /> : null;
};

export const SECTION_TYPES = Object.keys(RENDER);
